import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Brain, Activity, HeartPulse, ChevronDown, ChevronUp, Baby } from "lucide-react";
import { sevCls, qsofaCls } from "@/lib/scores";

const EYE = [[4, "Spontaneous"], [3, "To speech"], [2, "To pain"], [1, "None"]];
const VERBAL = [[5, "Oriented"], [4, "Confused"], [3, "Inappropriate words"], [2, "Incomprehensible"], [1, "None"]];
const MOTOR = [[6, "Obeys commands"], [5, "Localizes pain"], [4, "Withdraws"], [3, "Flexion"], [2, "Extension"], [1, "None"]];

const sel = "h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7]";

export function ScorePill({ score, label, cls, caption, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white p-3" data-testid={testid}>
      <div className={`flex h-12 w-12 flex-col items-center justify-center rounded-md text-white ${cls}`}>
        <span className="text-base font-bold leading-none">{score}</span>
        <span className="text-[8px] uppercase tracking-wide mt-0.5">{label}</span>
      </div>
      <p className="text-xs text-zinc-600 leading-snug">{caption}</p>
    </div>
  );
}

export function Collapsible({ title, open, onToggle, testid, children }) {
  return (
    <div className="border border-zinc-200 rounded-md p-5 mt-4">
      <button type="button" data-testid={testid} onClick={onToggle} className="w-full flex items-center justify-between">
        <span className="text-sm font-semibold text-zinc-900">{title}</span>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
}

export function PediatricNote() {
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 p-4" data-testid="pediatric-scores-note">
      <Baby className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <p className="text-sm text-amber-800">Pediatric patient detected — age-appropriate vital thresholds are applied. Adult ICU scores (qSOFA, SOFA, APACHE II) and the HEART/TIMI cardiac scores are not validated in children and are hidden; GCS is shown below.</p>
    </div>
  );
}

export function GcsCard({ eye, verbal, motor, setEye, setVerbal, setMotor, gcsTotal, gsev }) {
  return (
    <div className="border border-zinc-200 rounded-md p-5">
      <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Brain className="h-4 w-4 text-[#002FA7]" /> Glasgow Coma Scale</p>
      <p className="text-xs text-zinc-500 mt-0.5">Record especially if consciousness is impaired.</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5"><Label className="text-xs">Eye opening (E)</Label>
          <select data-testid="gcs-eye" className={sel} value={eye} onChange={(e) => setEye(Number(e.target.value))}>{EYE.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}</select></div>
        <div className="space-y-1.5"><Label className="text-xs">Verbal (V)</Label>
          <select data-testid="gcs-verbal" className={sel} value={verbal} onChange={(e) => setVerbal(Number(e.target.value))}>{VERBAL.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}</select></div>
        <div className="space-y-1.5"><Label className="text-xs">Motor (M)</Label>
          <select data-testid="gcs-motor" className={sel} value={motor} onChange={(e) => setMotor(Number(e.target.value))}>{MOTOR.map(([v, l]) => <option key={v} value={v}>{v} — {l}</option>)}</select></div>
      </div>
      <div className="mt-4"><ScorePill testid="gcs-result" score={gcsTotal} label="GCS" cls={gsev.cls} caption={`${gsev.label} · E${eye} V${verbal} M${motor}${gcsTotal <= 8 ? " — consider airway protection / ICU" : ""}`} /></div>
    </div>
  );
}

export function QsofaCard({ qTotal, qHigh }) {
  return (
    <div className="border border-zinc-200 rounded-md p-5 mt-4">
      <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2"><Activity className="h-4 w-4 text-[#002FA7]" /> qSOFA (sepsis risk)</p>
      <p className="text-xs text-zinc-500 mt-0.5">Auto from vitals + GCS: RR ≥ 22, SBP ≤ 100, altered mentation (GCS &lt; 15).</p>
      <div className="mt-4"><ScorePill testid="qsofa-result" score={qTotal} label="qSOFA" cls={qsofaCls(qTotal, qHigh)} caption={qHigh ? "≥ 2 — high risk; evaluate for sepsis / organ dysfunction" : "Low risk by qSOFA (reassess if deteriorating)"} /></div>
    </div>
  );
}

export function SofaCard({ open, onToggle, sf, updSf, setSf, sofaTotal }) {
  return (
    <Collapsible title="SOFA (organ dysfunction)" open={open} onToggle={onToggle} testid="sofa-toggle">
      <div data-testid="sofa-body">
        <p className="text-xs text-zinc-500 mb-3">CNS uses the GCS above. Enter the remaining organ parameters (blank = normal).</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">PaO₂ (mmHg)</Label><Input data-testid="sofa-pao2" inputMode="decimal" value={sf.pao2} onChange={updSf("pao2")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">FiO₂ (0–1)</Label><Input data-testid="sofa-fio2" inputMode="decimal" value={sf.fio2} onChange={updSf("fio2")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Platelets (×10³)</Label><Input data-testid="sofa-plt" inputMode="decimal" value={sf.plt} onChange={updSf("plt")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Bilirubin (mg/dL)</Label><Input data-testid="sofa-bili" inputMode="decimal" value={sf.bili} onChange={updSf("bili")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Creatinine (mg/dL)</Label><Input data-testid="sofa-creat" inputMode="decimal" value={sf.creat} onChange={updSf("creat")} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Cardiovascular</Label>
            <select data-testid="sofa-cardio" className={sel} value={sf.cardio} onChange={(e) => setSf((s) => ({ ...s, cardio: Number(e.target.value) }))}>
              <option value={0}>MAP ≥ 70</option>
              <option value={1}>MAP &lt; 70</option>
              <option value={2}>Dopamine ≤5 / dobutamine</option>
              <option value={3}>Dopamine &gt;5 / epi ≤0.1 / norepi ≤0.1</option>
              <option value={4}>Dopamine &gt;15 / epi &gt;0.1 / norepi &gt;0.1</option>
            </select></div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid="sofa-vent" checked={sf.vent} onCheckedChange={(v) => setSf((s) => ({ ...s, vent: !!v }))} /> On respiratory support (ventilation)</label>
        <div className="mt-4"><ScorePill testid="sofa-result" score={sofaTotal} label="SOFA" cls={sevCls(sofaTotal, 6, 11)} caption={`Aggregate organ-dysfunction score (0–24). Higher = greater ICU mortality risk.`} /></div>
      </div>
    </Collapsible>
  );
}

export function ApacheCard({ open, onToggle, ap, updAp, setAp, apacheTotal, caption }) {
  return (
    <Collapsible title="APACHE II (ICU severity)" open={open} onToggle={onToggle} testid="apache-toggle">
      <div data-testid="apache-body">
        <p className="text-xs text-zinc-500 mb-3">Temperature, MAP, HR, RR, age and GCS come from vitals/GCS above. Enter acute labs (blank = normal).</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["pao2", "PaO₂ (mmHg)"], ["fio2", "FiO₂ (0–1)"], ["ph", "Arterial pH"], ["na", "Na⁺"], ["k", "K⁺"], ["creat", "Creatinine"], ["hct", "Hematocrit %"], ["wbc", "WBC ×10³"]].map(([k, lbl]) => (
            <div key={k} className="space-y-1.5"><Label className="text-xs">{lbl}</Label><Input data-testid={`apache-${k}`} inputMode="decimal" value={ap[k]} onChange={updAp(k)} /></div>
          ))}
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid="apache-arf" checked={ap.arf} onCheckedChange={(v) => setAp((s) => ({ ...s, arf: !!v }))} /> Acute renal failure (doubles creatinine points)</label>
          <div className="flex items-center gap-2"><Label className="text-xs">Chronic health</Label>
            <select data-testid="apache-chronic" className={`${sel} w-auto`} value={ap.chronic} onChange={(e) => setAp((s) => ({ ...s, chronic: Number(e.target.value) }))}>
              <option value={0}>None</option><option value={2}>Severe comorbidity · elective post-op (+2)</option><option value={5}>Severe comorbidity · emergency/non-op (+5)</option>
            </select></div>
        </div>
        <div className="mt-4"><ScorePill testid="apache-result" score={apacheTotal} label="APACHE II" cls={sevCls(apacheTotal, 15, 25)} caption={caption} /></div>
      </div>
    </Collapsible>
  );
}

export function CardiacCard({ open, onToggle, ht, setHt, timi, setTimi, heartTotal, hBand, timiTotal, heartAgePts }) {
  return (
    <Collapsible title="Cardiac risk — HEART & TIMI (chest pain)" open={open} onToggle={onToggle} testid="cardiac-toggle">
      <div data-testid="cardiac-body">
        <p className="text-sm font-semibold text-zinc-900 flex items-center gap-2 mb-2"><HeartPulse className="h-4 w-4 text-[#002FA7]" /> HEART score</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">History</Label>
            <select data-testid="heart-history" className={sel} value={ht.history} onChange={(e) => setHt((s) => ({ ...s, history: Number(e.target.value) }))}><option value={0}>Slightly suspicious</option><option value={1}>Moderately suspicious</option><option value={2}>Highly suspicious</option></select></div>
          <div className="space-y-1.5"><Label className="text-xs">ECG</Label>
            <select data-testid="heart-ecg" className={sel} value={ht.ecg} onChange={(e) => setHt((s) => ({ ...s, ecg: Number(e.target.value) }))}><option value={0}>Normal</option><option value={1}>Non-specific repolarization</option><option value={2}>Significant ST deviation</option></select></div>
          <div className="space-y-1.5"><Label className="text-xs">Risk factors</Label>
            <select data-testid="heart-risk" className={sel} value={ht.risk} onChange={(e) => setHt((s) => ({ ...s, risk: Number(e.target.value) }))}><option value={0}>None</option><option value={1}>1–2 factors</option><option value={2}>≥3 factors / known atherosclerosis</option></select></div>
          <div className="space-y-1.5"><Label className="text-xs">Troponin</Label>
            <select data-testid="heart-trop" className={sel} value={ht.trop} onChange={(e) => setHt((s) => ({ ...s, trop: Number(e.target.value) }))}><option value={0}>≤ normal limit</option><option value={1}>1–3× normal</option><option value={2}>&gt;3× normal</option></select></div>
        </div>
        <p className="text-xs text-zinc-400 mt-2">Age points auto from patient age: {heartAgePts}.</p>
        <div className="mt-3"><ScorePill testid="heart-result" score={heartTotal} label="HEART" cls={hBand.cls} caption={hBand.label} /></div>

        <p className="text-sm font-semibold text-zinc-900 mt-6 mb-2">TIMI (UA/NSTEMI)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[["age65", "Age ≥ 65"], ["cad3", "≥3 CAD risk factors"], ["knownCad", "Known CAD (stenosis ≥50%)"], ["asa", "Aspirin use in prior 7 days"], ["angina2", "≥2 anginal episodes in 24h"], ["stDev", "ST deviation ≥0.5mm"], ["marker", "Positive cardiac marker"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid={`timi-${k}`} checked={timi[k]} onCheckedChange={(v) => setTimi((s) => ({ ...s, [k]: !!v }))} /> {lbl}</label>
          ))}
        </div>
        <div className="mt-3"><ScorePill testid="timi-result" score={timiTotal} label="TIMI" cls={sevCls(timiTotal, 3, 5)} caption={`0–7; higher = greater 14-day risk of adverse cardiac events.`} /></div>
      </div>
    </Collapsible>
  );
}

export function WellsCard({ open, onToggle, wells, setWells, hrOver100, wellsTotal, wellsLikely }) {
  return (
    <Collapsible title="Wells score (pulmonary embolism)" open={open} onToggle={onToggle} testid="wells-toggle">
      <div data-testid="wells-body">
        <p className="text-xs text-zinc-500 mb-3">HR &gt; 100 is auto from vitals ({hrOver100 ? "+1.5 applied" : "not met"}). Tick the clinical criteria.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[["signsDvt", "Clinical signs of DVT (+3)"], ["peLikely", "PE is the most likely diagnosis (+3)"], ["immob", "Immobilization ≥3d / surgery in 4 wks (+1.5)"], ["prevVte", "Previous DVT / PE (+1.5)"], ["hemoptysis", "Hemoptysis (+1)"], ["malignancy", "Active malignancy (+1)"]].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox data-testid={`wells-${k}`} checked={wells[k]} onCheckedChange={(v) => setWells((s) => ({ ...s, [k]: !!v }))} /> {lbl}</label>
          ))}
        </div>
        <div className="mt-3"><ScorePill testid="wells-result" score={wellsTotal} label="Wells" cls={wellsLikely ? "bg-red-600" : "bg-emerald-600"} caption={wellsLikely ? "PE likely (>4) — consider CT pulmonary angiography" : "PE unlikely (≤4) — consider D-dimer"} /></div>
      </div>
    </Collapsible>
  );
}

export function CurbCard({ open, onToggle, curb, setCurb, curbConfusion, curbTotal, band }) {
  return (
    <Collapsible title="CURB-65 (pneumonia severity)" open={open} onToggle={onToggle} testid="curb-toggle">
      <div data-testid="curb-body">
        <p className="text-xs text-zinc-500 mb-3">Confusion (GCS &lt; 15: {curbConfusion ? "yes" : "no"}), RR ≥ 30, BP (SBP &lt; 90 or DBP ≤ 60) and age ≥ 65 are auto from your entries. Enter BUN.</p>
        <div className="max-w-xs space-y-1.5">
          <Label className="text-xs">Blood urea nitrogen — BUN (mg/dL)</Label>
          <Input data-testid="curb-bun" inputMode="decimal" value={curb.bun} onChange={(e) => setCurb({ bun: e.target.value })} />
        </div>
        <div className="mt-3"><ScorePill testid="curb-result" score={curbTotal} label="CURB-65" cls={band.cls} caption={band.label} /></div>
      </div>
    </Collapsible>
  );
}
